import Button from "@bmi-digital/components/button";
import { useIsClient } from "@bmi-digital/components/hooks";
import HeaderComponent from "@bmi-digital/components/header";
import ArrowForwardIcon from "@bmi-digital/components/icon/ArrowForward";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { graphql, Link, withPrefix } from "gatsby";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import Toast from "@bmi-digital/components/toast";
import { RegionCode } from "@bmi-digital/components/language-selection";
import AuthService from "../auth/service";
import { useConfig } from "../contexts/ConfigProvider";
import { useBasketContext } from "../contexts/SampleBasketContext";
import useAuth from "../hooks/useAuth";
import { usePredictiveSearch } from "../hooks/usePredictiveSearch";
import { constructUrlWithPrevPage } from "../utils/myAccountUtils";
import { checkIfActiveLabelInParentNode } from "../utils/breadcrumbUtils";
import { pushToDataLayer, useGTM } from "../utils/google-tag-manager";
import { getPathWithCountryCode } from "../utils/path";
import { stringifyToObject } from "../utils/createActionLabelForAnalytics";
import { convertStrToBool } from "../utils/convertStrToBool";
import { executeSearchWithThreshold } from "../utils/search-utils/utils";
import Icon from "./Icon";
import RichText from "./RichText";
import SampleBasketDialog from "./SampleBasketDialog";
import { useSiteContext } from "./Site";
import { getCTA } from "./link/utils";
import Image from "./image/Image";
import type {
  ExternalLinkBlock,
  InternalLinkBlock,
  NavigationExternalLinkBlock,
  NavigationInternalLinkBlock
} from "./link/types";
import type { MicroCopyValues } from "@bmi-digital/dxb-microcopies";
import type { SiteData as SiteData } from "./Site";
import type { LanguageSelectionList } from "@bmi-digital/components/language-selection";
import type { ToastProps } from "@bmi-digital/components/toast";
import type { RichTextData } from "./RichText";
import type { HeaderProps } from "@bmi-digital/components/header";
import type { ButtonProps } from "@bmi-digital/components/button";
import type {
  PayloadNavigationHeading,
  PayloadNavigationMenu,
  PayloadNavigationRootMenu,
  PayloadNavigationSeparator,
  PayloadSampleOrder
} from "../types/payload";
import type { RegionJson } from "../schema/resolvers/types/regionJson";
import type { PageInfoHeader } from "./PageInfo";
import type { GetMicroCopy } from "./MicroCopy";
import type { NavigationList } from "@bmi-digital/components/navigation";
import type { PromoHeader } from "./Promo";

type NavigationSeparator = Pick<PayloadNavigationSeparator, "blockType">;

type NavigationHeading = Pick<PayloadNavigationHeading, "blockType" | "value">;

export type NavigationRootMenu = Pick<
  PayloadNavigationRootMenu,
  "blockType" | "label"
> & {
  links: (
    | NavigationSeparator
    | NavigationHeading
    | NavigationInternalLinkBlock
    | NavigationExternalLinkBlock
    | NavigationMenu
  )[];
  promos: {
    value: PromoHeader | PageInfoHeader;
  }[];
};

export type NavigationMenu = Pick<
  PayloadNavigationMenu,
  "blockType" | "label"
> & {
  links: (
    | NavigationSeparator
    | NavigationHeading
    | NavigationInternalLinkBlock
    | NavigationExternalLinkBlock
    | (Omit<NavigationMenu, "links"> & {
        links: (
          | NavigationSeparator
          | NavigationHeading
          | NavigationInternalLinkBlock
          | NavigationExternalLinkBlock
          | (Omit<NavigationMenu, "links"> & {
              links: (
                | NavigationSeparator
                | NavigationHeading
                | NavigationInternalLinkBlock
                | NavigationExternalLinkBlock
              )[];
            })
        )[];
      })
  )[];
};

export type CountryNavigation = { countryNavigationIntroduction: RichTextData };

export type SampleOrder = Pick<
  PayloadSampleOrder,
  "maximumSamples" | "title" | "path"
>;

type ParseMainNavigationProps = {
  navigationItems: (
    | NavigationInternalLinkBlock
    | NavigationExternalLinkBlock
    | NavigationHeading
    | NavigationSeparator
    | NavigationRootMenu
    | NavigationMenu
  )[];
  countryCode: string;
  getMicroCopy: GetMicroCopy;
  isClient?: boolean;
};

type ParseUtilitiesNavigationProps = {
  navigationItems: (InternalLinkBlock | ExternalLinkBlock)[];
  countryCode: string;
  getMicroCopy: GetMicroCopy;
  isClient?: boolean;
};

type GetPromoSectionProps = {
  promo: PromoHeader | PageInfoHeader;
  countryCode: SiteData["countryCode"];
  getMicroCopy: GetMicroCopy;
};

const getPromoSection = (props: GetPromoSectionProps) => {
  const { promo, countryCode, getMicroCopy } = props;
  const ctaLabelMicroCopy = getMicroCopy(microCopy.PAGE_LINK_LABEL);
  const cta = getCTA(promo, countryCode, ctaLabelMicroCopy);

  const label =
    promo.collectionType === "promo" ? promo.title || promo.name : promo.title;

  const ctaLabel =
    (promo.collectionType === "promo" && promo.cta?.[0]?.label) ||
    ctaLabelMicroCopy;

  return [
    ...("featuredMedia" in promo && promo.featuredMedia
      ? [
          {
            label,
            image: <Image {...promo.featuredMedia} />
          }
        ]
      : []),
    { label, isHeading: true },
    ...("subtitle" in promo && promo.subtitle
      ? [{ label: promo.subtitle, isParagraph: true }]
      : []),
    ...(cta
      ? [
          {
            label: ctaLabel,
            action: cta
          }
        ]
      : [])
  ];
};

const useHeaderAuthData = () => {
  const { auth0Domain, isLoginEnabled, intouchMyAccountRedirectUrl } =
    useConfig();
  const { isLoggedIn, profile } = useAuth();
  const { getMicroCopy, countryCode } = useSiteContext();

  const authHeaderProps: HeaderProps["auth"] = useMemo(() => {
    if (!isLoginEnabled || !intouchMyAccountRedirectUrl) {
      return;
    }

    return {
      isLoggedIn: Boolean(isLoggedIn && profile),
      myAccountBtnLabel: getMicroCopy(microCopy.MY_ACCOUNT_LABEL),
      myAccountBtnAction: {
        to: intouchMyAccountRedirectUrl,
        component: Link,
        gtm: {
          id: "nav-top-utilities-bar",
          label: getMicroCopy(microCopy.MY_ACCOUNT_LABEL),
          action: intouchMyAccountRedirectUrl
        }
      },
      loginBtnLabel: getMicroCopy(microCopy.LOG_IN_LABEL_BTN),
      loginButtonAction: {
        onClick: AuthService.login,
        gtm: {
          id: "nav-top-utilities-bar",
          label: getMicroCopy(microCopy.LOG_IN_LABEL_BTN),
          action: `https://${auth0Domain}/login`
        }
      },
      logoutBtnLabel: getMicroCopy(microCopy.LOG_OUT_LABEL_BTN),
      logoutButtonAction: {
        onClick: AuthService.logout,
        gtm: {
          id: "nav-top-utilities-bar",
          label: getMicroCopy(microCopy.LOG_OUT_LABEL_BTN),
          action: `https://${auth0Domain}/v2/logout`
        }
      }
    };
  }, [
    isLoggedIn,
    profile,
    getMicroCopy,
    isLoginEnabled,
    countryCode,
    intouchMyAccountRedirectUrl
  ]);

  return authHeaderProps;
};

const parseMainNavigationItems = ({
  navigationItems,
  countryCode,
  getMicroCopy,
  isClient
}: ParseMainNavigationProps): NavigationList[] => {
  if (!navigationItems || navigationItems.length === 0) {
    return [];
  }

  return navigationItems.reduce((result, item) => {
    if (item.blockType === "navigation-root-menu") {
      const { label, links, promos } = item;

      const navItem = {
        label,
        menu: parseMainNavigationItems({
          navigationItems: links,
          countryCode,
          getMicroCopy,
          isClient
        }),
        ...(promos &&
          promos.length && {
            footer: promos.flatMap((promo) =>
              getPromoSection({ promo: promo.value, countryCode, getMicroCopy })
            )
          }),
        gtm: {
          id: "nav-main-menu",
          label: label
        }
      };

      return result.concat(navItem);
    }

    if (item.blockType === "navigation-menu") {
      const { label, links } = item;

      const navItem = {
        label,
        menu: parseMainNavigationItems({
          navigationItems: links,
          countryCode,
          getMicroCopy,
          isClient
        }),
        gtm: {
          id: "nav-main-menu",
          label: label
        }
      };

      return result.concat(navItem);
    }

    if (item.blockType === "navigation-heading") {
      return result.concat({
        label: item.value,
        isLabelHidden: false,
        isHeading: true
      });
    }

    if (item.blockType === "navigation-separator") {
      if (result.length === 0) {
        return result;
      }
      const lastItem = result[result.length - 1];
      result[result.length - 1] = { ...lastItem, hasSeparator: true };
      return result;
    }

    if (item.blockType === "internal-link-block") {
      const { label, isLabelHidden, icon: iconName, linkedPage } = item;

      const to = getPathWithCountryCode(countryCode, linkedPage.value.path);
      return result.concat({
        label,
        ...(iconName && { icon: <Icon name={iconName} /> }),
        isLabelHidden,
        action: {
          to: to,
          component: Link,
          target: "_self"
        },
        gtm: {
          id: "nav-main-menu",
          label: label,
          action: stringifyToObject(to)
        }
      });
    }

    const href =
      isClient &&
      process.env.GATSBY_INTOUCH_ORIGIN &&
      item.url.includes(process.env.GATSBY_INTOUCH_ORIGIN)
        ? constructUrlWithPrevPage(item.url)
        : item.url;

    return result.concat({
      label: item.label,
      ...("icon" in item && item.icon && { icon: <Icon name={item.icon} /> }),
      ...("isLabelHidden" in item &&
        item.isLabelHidden && { isLabelHidden: item.isLabelHidden }),
      action: {
        href,
        target: "_blank"
      },
      gtm: {
        id: "nav-main-menu",
        label: item.label,
        action: href
      }
    });
  }, []);
};

const parseUtilitiesNavigationItems = ({
  navigationItems,
  countryCode,
  isClient
}: ParseUtilitiesNavigationProps): NavigationList[] => {
  if (!navigationItems || navigationItems.length === 0) {
    return [];
  }

  return navigationItems.reduce((result, item) => {
    if (item.blockType === "internal-link-block") {
      const { label, linkedPage } = item;

      const to = getPathWithCountryCode(countryCode, linkedPage.value.path);
      return result.concat({
        label,
        action: {
          to: to,
          component: Link,
          target: "_self"
        },
        gtm: {
          id: "nav-main-menu",
          label: label,
          action: stringifyToObject(to)
        }
      });
    }

    const href =
      isClient &&
      process.env.GATSBY_INTOUCH_ORIGIN &&
      item.url.includes(process.env.GATSBY_INTOUCH_ORIGIN)
        ? constructUrlWithPrevPage(item.url)
        : item.url;

    return result.concat({
      label: item.label,
      action: {
        href,
        target: "_blank"
      },
      gtm: {
        id: "nav-main-menu",
        label: item.label,
        action: href
      }
    });
  }, []);
};

const onCountrySelection = (label: string, code: string) =>
  pushToDataLayer({ id: "nav-country-selector", label: label, action: code });

export type Region = Pick<RegionJson, "label" | "regionCode" | "menu">;

const getRegionCode = (regionCode: string) => {
  switch (regionCode) {
    case "africa":
      return RegionCode.Africa;
    case "asia":
      return RegionCode.Asia;
    case "europe":
      return RegionCode.Europe;
    default:
      return RegionCode.Global;
  }
};

const Header = ({
  navigationData,
  utilitiesData,
  countryCode,
  activeLabel,
  countryNavigationIntroduction,
  regions,
  sampleOrder,
  lastNavigationLabel,
  disableSearch,
  variantCodeToPathMap
}: {
  navigationData: (
    | NavigationInternalLinkBlock
    | NavigationExternalLinkBlock
    | NavigationRootMenu
  )[];
  utilitiesData: (InternalLinkBlock | ExternalLinkBlock)[] | null;
  countryCode: string;
  activeLabel?: string;
  countryNavigationIntroduction?: RichTextData | null;
  regions: Region[];
  sampleOrder?: SampleOrder | null;
  lastNavigationLabel?: string;
  disableSearch?: boolean;
  variantCodeToPathMap?: Record<string, string>;
}) => {
  const { isClient } = useIsClient();
  const [toastOpen, setToastOpen] = useState<ToastProps["open"]>(false);
  const [toastMessage, setToastMessage] = useState<ToastProps["message"]>("");
  const toastRef = useRef<ToastProps["anchorEl"] | null>(null);

  const languages = useMemo<LanguageSelectionList[]>(() => {
    return regions.map((region) => ({
      ...region,
      regionCode: getRegionCode(region.regionCode),
      menu: region.menu.map((language) => ({
        ...language,
        icon: language.icon ? withPrefix(language.icon) : undefined
      }))
    }));
  }, [regions]);

  const language = useMemo(
    () =>
      languages
        .reduce<(typeof languages)[0]["menu"]>(
          (acc, { menu }) => acc.concat(menu),
          []
        )
        .find((l) => l.code === countryCode),
    [languages, countryCode]
  );

  const { getMicroCopy } = useSiteContext();
  const { isGatsbyDisabledElasticSearch, isSampleOrderingEnabled } =
    useConfig();
  const {
    basketState: { products: productsInBasket }
  } = useBasketContext();
  const authHeaderProps = useHeaderAuthData();

  const { suggestions, fetchSuggestions, handleSelection } =
    usePredictiveSearch(getMicroCopy, countryCode, variantCodeToPathMap);

  const handleSearchChange = useCallback(
    (val: unknown) =>
      executeSearchWithThreshold({
        term: val,
        fetcher: fetchSuggestions,
        minLength: 3,
        currentSuggestionsLength: suggestions.length
      }),
    [fetchSuggestions, suggestions]
  );

  useEffect(() => {
    const handleToast = (storageItem: string, message: MicroCopyValues) => {
      const showItem = localStorage.getItem(storageItem);
      if (convertStrToBool(showItem)) {
        setToastOpen(true);
        setToastMessage(getMicroCopy(message));
        localStorage.removeItem(storageItem);
      }
    };

    handleToast("showLogoutToast", microCopy.LOG_OUT_LABEL_ALERT);
  }, [getMicroCopy]);

  const handleToastClose = () => {
    setToastOpen(false);
  };

  if (!navigationData || !utilitiesData) {
    return null;
  }

  const utilities = parseUtilitiesNavigationItems({
    navigationItems: utilitiesData,
    countryCode,
    getMicroCopy,
    isClient
  });

  const navigation = parseMainNavigationItems({
    navigationItems: navigationData,
    countryCode,
    getMicroCopy,
    isClient
  });

  const basketUrl =
    sampleOrder && getPathWithCountryCode(countryCode, sampleOrder.path);

  const newActiveLabel =
    checkIfActiveLabelInParentNode(navigationData, lastNavigationLabel) ||
    activeLabel;
  return (
    <>
      <HeaderComponent
        disableSearch={isGatsbyDisabledElasticSearch || disableSearch}
        languages={languages}
        language={language}
        languageLabel={getMicroCopy(microCopy.MENU_LANGUAGE)}
        languageIntroduction={
          countryNavigationIntroduction && (
            <RichText
              hasNoBottomMargin
              document={countryNavigationIntroduction}
            />
          )
        }
        utilities={utilities}
        navigation={navigation}
        logoAction={{
          component: Link,
          to: getPathWithCountryCode(countryCode, "")
        }}
        logoLabel={getMicroCopy(microCopy.GLOBAL_LOGO_LABEL)}
        activeNavLabel={newActiveLabel}
        closeLabel={getMicroCopy(microCopy.GLOBAL_CLOSE)}
        searchGtm={{
          id: "search1",
          label: getMicroCopy(microCopy.SEARCH_LABEL)
        }}
        navUtilityLinkButton={(props: ButtonProps) => (
          <Button
            gtm={{
              id: "nav-top-utilities-bar",
              action: stringifyToObject(props.to) || props.href,
              label: React.Children.toArray(props.children).join("")
            }}
            {...props}
          />
        )}
        shoppingCartCount={productsInBasket.length}
        basketLabel={getMicroCopy(microCopy.BASKET_LABEL)}
        sampleBasketDialog={
          isSampleOrderingEnabled && sampleOrder
            ? (props: { toggleCart: () => void }) => (
                <SampleBasketDialog
                  title={sampleOrder.title}
                  basketUrl={basketUrl}
                  maximumSamples={sampleOrder.maximumSamples}
                  {...props}
                />
              )
            : undefined
        }
        navigationButtonComponent={(props: ButtonProps) => (
          <Button
            gtm={{
              id: "nav-main-menu",
              action: stringifyToObject(props.to),
              label:
                typeof props.children !== "string"
                  ? props.accessibilityLabel
                  : undefined
            }}
            {...props}
          />
        )}
        promoButtonComponent={({ startIcon, ...props }: ButtonProps) => (
          <Button
            {...props}
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            className="Button"
            style={{ marginLeft: 16, marginBottom: 15 }}
          />
        )}
        languageDropdownCloseButtonGtm={{
          id: "nav-country-selector",
          label: "close panel",
          action: "close panel"
        }}
        onCountrySelection={onCountrySelection}
        useGTM={useGTM}
        searchAction={getPathWithCountryCode(countryCode, "search")}
        searchLabel={getMicroCopy(microCopy.SEARCH_LABEL)}
        searchPlaceholder={getMicroCopy(microCopy.SEARCH_PLACEHOLDER_HEADER)}
        searchTitle={getMicroCopy(microCopy.SEARCH_TITLE)}
        searchSuggestions={suggestions}
        onChangeSearch={handleSearchChange}
        onSelectedSearchSuggestion={handleSelection}
        openLabel={getMicroCopy(microCopy.MENU_OPEN)}
        mainMenuTitleLabel={getMicroCopy(microCopy.MENU_MAIN_TITLE)}
        mainMenuDefaultLabel={getMicroCopy(microCopy.MENU_MAIN_DEFAULT)}
        auth={authHeaderProps}
        ref={toastRef}
      />
      {toastRef.current && (
        <Toast
          closeButtonAriaLabel={getMicroCopy(microCopy.GLOBAL_CLOSE)}
          message={toastMessage}
          type="success"
          open={toastOpen}
          onClose={handleToastClose}
          anchorEl={toastRef.current}
        />
      )}
    </>
  );
};

export default Header;

export const query = graphql`
  fragment HeaderNavigationRootMenuFragment on PayloadNavigationRootMenu {
    blockType
    label
    links {
      ... on PayloadNavigationSeparator {
        blockType
      }
      ... on PayloadInternalLinkBlock {
        ...InternalLinkBlockFragment
      }
      ... on PayloadExternalLinkBlock {
        ...ExternalLinkBlockFragment
      }
      ... on PayloadNavigationHeading {
        blockType
        value
      }
      ... on PayloadNavigationMenu {
        blockType
        label
        links {
          ... on PayloadNavigationSeparator {
            blockType
          }
          ... on PayloadNavigationHeading {
            blockType
            value
          }
          ... on PayloadInternalLinkBlock {
            ...InternalLinkBlockFragment
          }
          ... on PayloadExternalLinkBlock {
            ...ExternalLinkBlockFragment
          }
          ... on PayloadNavigationMenu {
            blockType
            label
            links {
              ... on PayloadNavigationSeparator {
                blockType
              }
              ... on PayloadNavigationHeading {
                blockType
                value
              }
              ... on PayloadInternalLinkBlock {
                ...InternalLinkBlockFragment
              }
              ... on PayloadExternalLinkBlock {
                ...ExternalLinkBlockFragment
              }
              ... on PayloadNavigationMenu {
                blockType
                label
                links {
                  ... on PayloadNavigationSeparator {
                    blockType
                  }
                  ... on PayloadNavigationHeading {
                    blockType
                    value
                  }
                  ... on PayloadInternalLinkBlock {
                    ...InternalLinkBlockFragment
                  }
                  ... on PayloadExternalLinkBlock {
                    ...ExternalLinkBlockFragment
                  }
                }
              }
            }
          }
        }
      }
    }
    promos {
      value {
        ... on PayloadPromo {
          ...PromoHeaderFragment
        }
        ... on PayloadPage {
          ...PageInfoHeaderFragment
        }
      }
    }
  }
  fragment RegionFragment on RegionJson {
    label
    regionCode
    menu {
      code
      label
      icon
    }
  }
  fragment CountryNavigationFragment on PayloadCountryNavigation {
    countryNavigationIntroduction {
      ...RichTextFragment
    }
  }
  fragment SampleOrderFragment on PayloadSampleOrder {
    maximumSamples
    title
    path
  }
`;
